/** Marker MIME type for a completed download that was streamed to disk (not held in RAM). */
export const DISK_SAVED_BLOB_TYPE = 'application/x.amm.saved-to-disk';

/** Positional write so out-of-order Range chunks can land at their byte offset. */
export type FileSystemWriteData =
  | BufferSource
  | Blob
  | string
  | { type: 'write'; position: number; data: BufferSource | Blob | string };

export type FileSystemWritableFileStreamLike = {
  write(data: FileSystemWriteData): Promise<void>;
  close(): Promise<void>;
  abort(): Promise<void>;
};

export interface DownloadStreamOptions {
  knownTotalBytes?: number;
  /** Suggested filename for the save picker / Blob fallback. */
  fileName?: string;
  /**
   * Prefer writing Range chunks to a local file via File System Access API.
   * Opens the save picker when no `writable` is supplied.
   * Falls back to in-memory Blob when unsupported or picker fails (non-cancel).
   */
  streamToDisk?: boolean;
  /**
   * Pre-opened writable from a user gesture (preferred when prepare/auth runs first).
   * When set, chunks are written here and not buffered in RAM.
   */
  writable?: FileSystemWritableFileStreamLike | null;
}

export function isSavedToDiskBlob(blob: Blob | null | undefined): boolean {
  return !!blob && blob.type === DISK_SAVED_BLOB_TYPE;
}

export function supportsDiskDownloadStreaming(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof (window as any).showSaveFilePicker === 'function' &&
    window.isSecureContext === true
  );
}

/**
 * Opens a Save-as picker and returns a writable stream, or null if unsupported.
 * Re-throws AbortError when the user cancels.
 */
export async function openDiskWritable(
  suggestedName: string,
  _mimeType = 'application/octet-stream'
): Promise<FileSystemWritableFileStreamLike | null> {
  if (!supportsDiskDownloadStreaming()) {
    return null;
  }

  const safeName = (suggestedName || 'download').replace(/[/\\?%*:|"<>]/g, '_');

  try {
    const handle = await (window as any).showSaveFilePicker({
      suggestedName: safeName,
    });
    return (await handle.createWritable()) as FileSystemWritableFileStreamLike;
  } catch (err: any) {
    if (err?.name === 'AbortError') {
      throw err;
    }
    console.warn('Disk download picker unavailable; falling back to memory.', err);
    return null;
  }
}

export function createDiskSavedBlob(): Blob {
  return new Blob([], { type: DISK_SAVED_BLOB_TYPE });
}

/**
 * Extracts the file name from a Content-Disposition header.
 * Prefers RFC 5987 `filename*=UTF-8''...` (set by the API from the file table),
 * falling back to plain `filename="..."`. Returns null when absent.
 */
export function fileNameFromContentDisposition(header: string | null | undefined): string | null {
  if (!header) {
    return null;
  }

  const extMatch = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header);
  if (extMatch?.[1]) {
    try {
      const decoded = decodeURIComponent(extMatch[1].trim());
      if (decoded) return decoded;
    } catch {
      /* fall through to plain filename */
    }
  }

  const plainMatch = /filename\s*=\s*"([^"]+)"/.exec(header) || /filename\s*=\s*([^;]+)/.exec(header);
  const plain = plainMatch?.[1]?.trim();
  return plain || null;
}
